import { z } from 'zod';
import * as enums from './enums';
import { SystemUserRoles } from './enums';
import { paginated } from './utils';
import { clearable, currentPasswordSchema, emailSchema, fileNameSchema, firstNameSchema, httpUrlSchema, isoDateTimeSchema, lastNameSchema, nameSchema, newPasswordSchema, numberSchema, organizationNameSchema, phoneNumberSchema, postalCodeSchema, stringSchema, uuidSchema, } from './validation-utils';
export * from './enums';
export * from './reporting';
export * from './survey';
/**
 * ENTITIES
 */
export const Industry = z
    .object({
    id: enums.Industries,
    displayName: z.string(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const Prefecture = z
    .object({
    id: enums.Prefectures,
    displayName: z.string(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const CompanySize = z
    .object({
    id: enums.CompanySizes,
    displayName: z.string(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const UserRole = z
    .object({
    id: enums.UserRoles,
    displayName: z.string(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
const systemUserRoleFields = z.object({
    id: SystemUserRoles,
    name: z.string(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const SystemUserRole = systemUserRoleFields.readonly();
export const CreateSystemUserRole = systemUserRoleFields.omit({ updatedAt: true }).readonly();
const systemUserFields = z.object({
    uuid: uuidSchema,
    lastName: z.string(),
    firstName: z.string(),
    email: z.string(),
    phoneNumber: z.string().nullable(),
    isActive: z.boolean(),
    role: SystemUserRole,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const SystemUser = systemUserFields.readonly();
export const CreateSystemUser = systemUserFields.omit({ updatedAt: true, role: true }).readonly();
const userFields = z.object({
    uuid: uuidSchema,
    organizationId: uuidSchema,
    lastName: z.string(),
    firstName: z.string(),
    email: z.string(),
    phoneNumber: z.string().nullable(),
    department: z.string().nullable(),
    position: z.string().nullable(),
    role: UserRole,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const User = userFields.readonly();
export const CreateUser = userFields.omit({ updatedAt: true }).readonly();
export const OrganizationContact = z
    .object({
    organizationId: uuidSchema,
    lastName: z.string(),
    firstName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const Organization = z
    .object({
    uuid: uuidSchema,
    name: z.string(),
    industry: Industry,
    website: z.string().nullable(),
    assignedConsultant: SystemUser.nullable(),
    assignedSales: z.string().nullable(),
    postalCode: z.string().nullable(),
    prefecture: Prefecture,
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    companySize: CompanySize,
    contact: OrganizationContact.nullable(),
    mfaEnabled: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const UploaderType = z.enum(['USER', 'SYSTEM_USER']);
export const UploaderInfo = z
    .object({
    name: z.string(),
    userType: UploaderType,
})
    .readonly();
export const UploadedFile = z
    .object({
    fileId: uuidSchema,
    organizationId: uuidSchema,
    fileName: z.string(),
    contentType: z.string(),
    fileSizeBytes: z.number(),
    uploadedBy: UploaderInfo.nullable(),
    uploadedAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
/**
 * API REQUEST VALIDATION SCHEMAS
 */
export const CreateUserRequestBody = z.object({
    lastName: lastNameSchema,
    firstName: firstNameSchema,
    email: emailSchema,
    phoneNumber: clearable(phoneNumberSchema),
    department: clearable(stringSchema),
    position: clearable(stringSchema),
    roleId: enums.UserRoles,
});
export const GetUserListRequestQuery = z.object({
    from: numberSchema.optional(),
    limit: numberSchema.max(100).optional(),
    search: stringSchema.optional(),
});
export const GetUserRequestParams = z.object({
    uuid: uuidSchema,
});
export const ResetUserMfaRequestParams = z.object({
    uuid: uuidSchema,
});
export const UpdateUserRequestBody = z.object({
    lastName: lastNameSchema.optional(),
    firstName: firstNameSchema.optional(),
    email: emailSchema.optional(),
    phoneNumber: clearable(phoneNumberSchema),
    department: clearable(stringSchema),
    position: clearable(stringSchema),
    roleId: enums.UserRoles.optional(),
});
export const CreateSystemUserRequestBody = z.object({
    lastName: lastNameSchema,
    firstName: firstNameSchema,
    email: emailSchema,
    phoneNumber: clearable(phoneNumberSchema),
    roleId: SystemUserRoles,
});
export const GetSystemUserListRequestQuery = z.object({
    from: numberSchema.optional(),
    limit: numberSchema.max(100).optional(),
    search: stringSchema.optional(),
});
export const GetSystemUserRequestParams = z.object({
    uuid: uuidSchema,
});
export const UpdateSystemUserRequestBody = z.object({
    lastName: lastNameSchema.optional(),
    firstName: firstNameSchema.optional(),
    email: emailSchema.optional(),
    phoneNumber: clearable(phoneNumberSchema),
    roleId: SystemUserRoles.optional(),
});
export const ChangePasswordRequestBody = z.object({
    currentPassword: currentPasswordSchema,
    newPassword: newPasswordSchema,
});
export const ResetSystemUserMfaRequestParams = z.object({
    uuid: uuidSchema,
});
export const CreateSystemUserRoleRequestBody = z.object({
    name: nameSchema,
});
export const GetSystemUserRoleListRequestQuery = z.object({
    from: numberSchema.optional(),
    limit: numberSchema.max(100).optional(),
});
export const GetSystemUserRoleRequestParams = z.object({
    uuid: uuidSchema,
});
export const CreateOrganizationRequestBody = z.object({
    name: organizationNameSchema,
    industryId: enums.Industries,
    website: clearable(httpUrlSchema),
    postalCode: clearable(postalCodeSchema),
    prefectureId: enums.Prefectures,
    address1: clearable(stringSchema),
    address2: clearable(stringSchema),
    companySizeId: enums.CompanySizes,
    assignedConsultantId: clearable(uuidSchema),
    assignedSales: clearable(nameSchema),
    mfaEnabled: z.boolean().optional(),
});
export const CreateGeneralUserRequestParams = z.object({
    organizationId: uuidSchema,
});
export const GetOrganizationListRequestQuery = z.object({
    from: numberSchema.optional(),
    limit: numberSchema.max(100).optional(),
    search: stringSchema.optional(),
    assignedConsultantIds: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(uuidSchema))
        .optional(),
    industryIds: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(enums.Industries))
        .optional(),
    prefectureIds: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(enums.Prefectures))
        .optional(),
    companySizeIds: stringSchema
        .transform((value) => JSON.parse(value))
        .pipe(z.array(enums.CompanySizes))
        .optional(),
});
export const GetOrganizationRequestParams = z.object({
    uuid: uuidSchema,
});
export const UpdateOrganizationRequestBody = z.object({
    name: organizationNameSchema.optional(),
    industryId: enums.Industries.optional(),
    website: clearable(httpUrlSchema),
    postalCode: clearable(postalCodeSchema),
    prefectureId: enums.Prefectures.optional(),
    address1: clearable(stringSchema),
    address2: clearable(stringSchema),
    companySizeId: enums.CompanySizes.optional(),
    assignedConsultantId: clearable(uuidSchema),
    assignedSales: clearable(nameSchema),
});
export const GetOrganizationContactRequestParams = z.object({
    organizationId: uuidSchema,
});
export const CreateOrganizationContactRequestBody = z.object({
    lastName: lastNameSchema,
    firstName: clearable(firstNameSchema),
    email: clearable(emailSchema),
    phone: clearable(phoneNumberSchema),
});
export const UpdateOrganizationContactRequestBody = CreateOrganizationContactRequestBody;
export const CreateGeneralUserRequestBody = z.object({
    lastName: lastNameSchema,
    firstName: firstNameSchema,
    email: emailSchema,
    phoneNumber: clearable(phoneNumberSchema),
    department: clearable(stringSchema),
    position: clearable(stringSchema),
    roleId: enums.UserRoles,
});
export const GetGeneralUserListRequestParams = z.object({
    organizationId: uuidSchema,
});
export const GetGeneralUserListRequestQuery = z.object({
    from: numberSchema.optional(),
    limit: numberSchema.max(100).optional(),
});
export const GetGeneralUserRequestParams = z.object({
    organizationId: uuidSchema,
    userId: uuidSchema,
});
export const UpdateGeneralUserRequestBody = z.object({
    lastName: lastNameSchema.optional(),
    firstName: firstNameSchema.optional(),
    email: emailSchema.optional(),
    phoneNumber: clearable(phoneNumberSchema),
    department: clearable(stringSchema),
    position: clearable(stringSchema),
    roleId: enums.UserRoles.optional(),
});
export const ResetGeneralUserMfaRequestParams = z.object({
    organizationId: uuidSchema,
    userId: uuidSchema,
});
// File upload schemas
export const GetFileListRequestParams = z.object({
    organizationId: uuidSchema,
});
export const GetFileListRequestQuery = z.object({
    from: numberSchema.optional(),
    limit: numberSchema.max(100).optional(),
});
export const GetFileRequestParams = z.object({
    organizationId: uuidSchema,
    fileId: uuidSchema,
});
export const CreateUploadUrlRequestParams = z.object({
    organizationId: uuidSchema,
});
export const CreateUploadUrlRequestBody = z.object({
    contentType: stringSchema.nonempty(),
});
export const ConfirmUploadRequestParams = z.object({
    organizationId: uuidSchema,
    fileId: uuidSchema,
});
export const ConfirmUploadRequestBody = z.object({
    fileName: fileNameSchema,
    contentType: stringSchema.nonempty(),
    fileSizeBytes: numberSchema.positive(),
});
/**
 * API RESPONSE TYPES
 */
/**
 * The body of every failed request. `generalErrorHandlerHook` on the backend
 * scrubs thrown errors to this shape, so one definition covers every status:
 * `error` and `message` are both derived from the status code and never carry
 * internal detail.
 */
export const ErrorResponse = z
    .object({
    statusCode: z.number(),
    error: z.string(),
    message: z.string(),
})
    .readonly();
export const GetIndustryListResponse = z.array(Industry);
export const GetPrefectureListResponse = z.array(Prefecture);
export const GetCompanySizeListResponse = z.array(CompanySize);
export const GetUserRoleListResponse = z.array(UserRole);
export const CreateUserResponse = CreateUser;
export const GetUserResponse = User;
export const GetUserListResponse = paginated(User);
export const UpdateUserResponse = User;
export const CreateSystemUserResponse = CreateSystemUser;
export const GetSystemUserResponse = SystemUser;
export const GetSystemUserListResponse = paginated(SystemUser);
export const UpdateSystemUserResponse = SystemUser;
export const CreateSystemUserRoleResponse = CreateSystemUserRole;
export const GetSystemUserRoleResponse = SystemUserRole;
export const GetSystemUserRoleListResponse = paginated(SystemUserRole);
export const UpdateSystemUserRoleResponse = SystemUserRole;
export const CreateOrganizationResponse = Organization;
export const GetOrganizationResponse = Organization;
export const GetOrganizationListResponse = paginated(Organization);
export const UpdateOrganizationResponse = Organization;
export const ToggleMfaResponse = z
    .object({
    mfaEnabled: z.boolean(),
    updatedUsers: z.number(),
})
    .readonly();
export const CreateOrganizationContactResponse = OrganizationContact;
export const UpdateOrganizationContactResponse = OrganizationContact;
export const CreateGeneralUserResponse = CreateUser;
export const GetGeneralUserListResponse = paginated(User);
export const GetGeneralUserResponse = User;
export const UpdateGeneralUserResponse = User;
// ── File upload responses ─────────────────────────
export const CreateUploadUrlResponse = z
    .object({
    fileId: uuidSchema,
    uploadUrl: z.string(),
    expiresAt: isoDateTimeSchema,
})
    .readonly();
export const ConfirmUploadResponse = UploadedFile;
export const GetFileResponse = UploadedFile;
export const GetFileListResponse = paginated(UploadedFile);
export const GetFileDownloadUrlResponse = z
    .object({
    downloadUrl: z.string(),
    expiresAt: isoDateTimeSchema,
})
    .readonly();
//# sourceMappingURL=index.js.map